/**
 * Pure H OS · Pre-Production Gap Closure · Formula Comparison.
 * Side-by-side compare of two formulas — top/heart/base notes,
 * dosage %, IFRA limits, suppliers. Reuses live FORMULAS data.
 */
import { useMemo, useState } from "react";
import { Card, SectionHeader, SeverityBadge } from "../../components/lib";
import { FORMULAS, Formula, FormulaNoteRow, RND_PRODUCTS } from "../../mocks/rndData";

function nameOf(f: Formula): string {
  return RND_PRODUCTS.find((p) => p.id === f.productId)?.name ?? f.productId;
}

type PickerProps = { label: string; value: string; onChange: (id: string) => void; testId: string };
function FormulaPicker({ label, value, onChange, testId }: PickerProps) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-[0.16em] mb-1" style={{ color: "var(--ph-muted)" }}>{label}</div>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testId}
        className="w-full text-sm border rounded p-2 outline-none"
        style={{ borderColor: "var(--ph-border-subtle)", background: "var(--ph-surface)", color: "var(--ph-text)" }}
      >
        {FORMULAS.map((f) => (
          <option key={f.id} value={f.id}>{nameOf(f)} · v{f.version}</option>
        ))}
      </select>
    </div>
  );
}

function severityForIfra(row: FormulaNoteRow): "low" | "medium" | "high" {
  if (row.ifraLimitPct === undefined) return "low";
  if (row.dosagePct > row.ifraLimitPct) return "high";
  if (row.dosagePct > row.ifraLimitPct * 0.9) return "medium";
  return "low";
}

function NotesTable({ label, aRows, bRows, testId }: { label: string; aRows: FormulaNoteRow[]; bRows: FormulaNoteRow[]; testId: string }) {
  // Union of component names
  const names = Array.from(new Set([...aRows.map((r) => r.component), ...bRows.map((r) => r.component)]));
  return (
    <div data-testid={testId}>
      <div className="text-[10px] uppercase tracking-[0.16em] mb-2" style={{ color: "var(--ph-muted)" }}>{label}</div>
      <div className="rounded overflow-hidden" style={{ border: "1px solid var(--ph-border-subtle)" }}>
        <div className="grid grid-cols-5 text-[10px] uppercase tracking-[0.14em] px-3 py-2" style={{ background: "var(--ph-surface-subtle)", color: "var(--ph-muted)" }}>
          <div className="col-span-2">Component</div><div>A</div><div>B</div><div>Δ / IFRA</div>
        </div>
        {names.map((n) => {
          const a = aRows.find((r) => r.component === n);
          const b = bRows.find((r) => r.component === n);
          const av = a?.dosagePct ?? 0;
          const bv = b?.dosagePct ?? 0;
          const delta = bv - av;
          const ifra = a?.ifraLimitPct ?? b?.ifraLimitPct;
          const flag = (a && severityForIfra(a) === "high") || (b && severityForIfra(b) === "high") ? "high"
            : (a && severityForIfra(a) === "medium") || (b && severityForIfra(b) === "medium") ? "medium" : "low";
          return (
            <div key={n} className="grid grid-cols-5 text-xs px-3 py-2 tabular-nums" style={{ borderTop: "1px solid var(--ph-border-subtle)" }}>
              <div className="col-span-2" style={{ color: "var(--ph-text)" }}>{n}</div>
              <div style={{ color: a ? "var(--ph-text)" : "var(--ph-muted)" }}>{a ? `${av.toFixed(2)}%` : "—"}</div>
              <div style={{ color: b ? "var(--ph-text)" : "var(--ph-muted)" }}>{b ? `${bv.toFixed(2)}%` : "—"}</div>
              <div className="flex items-center gap-2">
                <span style={{ color: delta > 0 ? "var(--ph-warning)" : delta < 0 ? "var(--ph-accent)" : "var(--ph-muted)" }}>
                  {delta === 0 ? "—" : `${delta > 0 ? "+" : ""}${delta.toFixed(2)}%`}
                </span>
                {ifra !== undefined && <SeverityBadge severity={flag} />}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default function FormulaCompare() {
  const [aId, setAId] = useState<string>(FORMULAS[0]?.id ?? "");
  const [bId, setBId] = useState<string>(FORMULAS[1]?.id ?? FORMULAS[0]?.id ?? "");
  const a: Formula | undefined = useMemo(() => FORMULAS.find((f) => f.id === aId), [aId]);
  const b: Formula | undefined = useMemo(() => FORMULAS.find((f) => f.id === bId), [bId]);

  return (
    <div className="space-y-6" data-testid="formula-compare-page">
      <Card testId="fc-pickers">
        <SectionHeader eyebrow="Formula Comparison" title="Pick two formulas to compare" />
        <div className="grid grid-cols-2 gap-4 mt-4">
          <FormulaPicker label="Formula A" value={aId} onChange={setAId} testId="fc-picker-a" />
          <FormulaPicker label="Formula B" value={bId} onChange={setBId} testId="fc-picker-b" />
        </div>
      </Card>

      {a && b && (
        <Card testId="fc-summary">
          <div className="grid grid-cols-2 gap-6">
            <div>
              <div className="text-[10px] uppercase tracking-[0.16em]" style={{ color: "var(--ph-muted)" }}>Formula A</div>
              <div className="text-base font-semibold" style={{ color: "var(--ph-text)" }}>{nameOf(a)}</div>
              <div className="text-xs" style={{ color: "var(--ph-muted)" }}>v{a.version} · total {a.totalPct.toFixed(1)}% · IFRA {a.ifraCompliant ? "OK" : "flag"}</div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-[0.16em]" style={{ color: "var(--ph-muted)" }}>Formula B</div>
              <div className="text-base font-semibold" style={{ color: "var(--ph-text)" }}>{nameOf(b)}</div>
              <div className="text-xs" style={{ color: "var(--ph-muted)" }}>v{b.version} · total {b.totalPct.toFixed(1)}% · IFRA {b.ifraCompliant ? "OK" : "flag"}</div>
            </div>
          </div>
        </Card>
      )}

      {a && b && (
        <Card testId="fc-notes">
          <div className="space-y-6">
            <NotesTable label="Top notes"   aRows={a.topNotes}   bRows={b.topNotes}   testId="fc-notes-top" />
            <NotesTable label="Heart notes" aRows={a.heartNotes} bRows={b.heartNotes} testId="fc-notes-heart" />
            <NotesTable label="Base notes"  aRows={a.baseNotes}  bRows={b.baseNotes}  testId="fc-notes-base" />
          </div>
        </Card>
      )}
    </div>
  );
}
